import Phaser from 'phaser';
import * as Tone from 'tone';
import { colors } from 'Phaser/config';
import {
  EventBus,
  ReactToPhaserListeners,
  NotifyReactEvents,
  RemoveListener,
} from 'Phaser/EventBus2';
import Instruction, { InstructionPosition } from 'Phaser/GameObjects/Instruction';
import { Timestamp } from 'Types/ExerciseData';
import { isCapacitorNative } from 'Utils/Constants';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { feedbackAdvanceDelayMs } from 'Utils/feedbackTiming';
import { messageTopBelowNotation } from 'Utils/resultMessagePlacement';
import { createActor, type ActorRefFrom } from 'xstate';
import { exerciseMachine, type ExerciseMachine } from 'Phaser/ExerciseMachine/exerciseMachine';
import { buildSceneActions } from 'Phaser/ExerciseMachine/buildSceneActions';
import type { ExerciseEvent, ExercisePhase } from 'Phaser/ExerciseMachine/exerciseMachine.types';
import type { IExerciseSceneFacade } from 'Phaser/ExerciseMachine/IExerciseSceneFacade';
import { soundEffectConfig } from 'Volume/volumeOutput';
import type { TutorialInputUsed } from 'Types/EventTypes';
import { TOUCH_TAP_LATE_ALLOWANCE_MS, tapInputHint } from 'Utils/playAlong/tapInput';
import { isTouchDevice } from 'Utils/UserAgent';
import {
  NEXT_RING_AFTER_TIMED_RUN_MS,
  TUTORIAL_COUNT_IN_START_DELAY_MS,
  strongerInput,
} from 'Utils/playAlong/tutorialPlayAlong';
import CountInCountdownView, {
  type CountdownAnchor,
  type RingTarget,
} from 'Phaser/Gameplay/CountInCountdownView';
import type { CountedClicks } from 'Models/CountInCountdown';

const FEEDBACK_TYPES = [
  'great',
  'great_job',
  'good_job',
  'nice',
  'excellent',
  'nicely_done',
  'well_done',
  'perfect',
];

function feedbackKeyToFilename(key: string): string {
  const match = key.match(/^feedback(\d+)_0(\d+)$/);
  if (!match) return key;
  const typeIdx = parseInt(match[1], 10);
  const variantIdx = match[2];
  const typeName = FEEDBACK_TYPES[typeIdx];
  return typeName ? `${typeName}_0${variantIdx}` : key;
}

// MUS-113: on web, if a feedback clip's duration cannot be read (e.g. the
// HTML5 audio fallback backend, or a decode miss) fall back to the pre-fix
// 1000ms so advance timing is never worse than it was before the fix.
const FEEDBACK_DURATION_FALLBACK_SECS = 1;

// On web the tutorial video plays the narration between two objectives and its
// StartExercise cue starts the exercise. If that cue never arrives (the video
// could not seek or play), start anyway this long after the narration should
// have ended, so the player is never left with a phrase and no cursor.
export const WEB_NARRATION_START_FALLBACK_MS = 3000;
// The tutorial's speed control runs from 0.5x to 2x. The fallback allows for
// the slowest, whatever the speed is: it must never start an exercise while
// the narration is still playing.
export const SLOWEST_VIDEO_PLAYBACK_RATE = 0.5;

export interface IObjective {
  text?: string;
  instructionWaitTime?: number;
  videoSkipDestination?: Timestamp;
  videoCompleteDestination?: Timestamp;
  narrationStartTimestamp?: Timestamp;
  narrationEndTimestamp?: Timestamp;
}

export interface IConfig<Objective extends IObjective> {
  chapterIdx?: number;
  levelNumber?: number;
  playbackSpeed?: number;
  objectives: Objective[];
  firstObjectiveAppearDelay?: number;
  /**
   * Whether the app can hear the player (a MIDI keyboard). When false the
   * exercise plays itself: see Utils/playAlong/tutorialPlayAlong. Absent
   * means graded.
   */
  graded?: boolean;
}

export default abstract class ExerciseBase<
  Objective extends IObjective,
  Config extends IConfig<Objective>,
>
  extends Phaser.Scene
  implements IExerciseSceneFacade
{
  active: boolean = false;
  instruction: Instruction | null = null;
  numFailedAttempts: number = 0;
  failMessage: Phaser.GameObjects.Text | null = null;
  toastMessage: Phaser.GameObjects.Text | null = null;
  debugText: Phaser.GameObjects.Text | null = null;
  paused: boolean = false;
  config?: Config;
  currentObjective?: number;
  waitToast: boolean = false;

  registeredKeys: Map<string, Phaser.Input.Keyboard.Key> = new Map();
  skipped: boolean = false;
  videoResumeTimeout?: Phaser.Time.TimerEvent;
  /** False when the app cannot hear the player: the exercise plays itself. */
  graded: boolean = true;

  // Ungraded: passes run on the current objective, and the pause before the
  // first count-in.
  private playAlongPassesThisObjective = 0;
  private countInStartDelay?: Phaser.Time.TimerEvent;
  // Ungraded: the count-in's countdown number and closing ring.
  private countInCountdownInstance?: CountInCountdownView;

  // Tap input: whether a tap was heard in the pass running now, and on the
  // objective on screen.
  private tapsThisPass = false;
  private tapsThisObjective = false;
  // The strongest input heard on the objectives before the one on screen,
  // for the chapter-completed event (it describes the whole exercise).
  private inputBeforeThisObjective: TutorialInputUsed = 'none';
  // Ungraded: whether an objective of this exercise passed on a pass the app
  // heard (taps). Moving on from the last objective with Next then still
  // clears the chapter on a graded pass.
  private passedOnAHeardPass = false;

  protected boundCallbacks: Map<string, (...args: never[]) => void> = new Map();

  // ---- State Machine ----
  private exerciseActor: ActorRefFrom<ExerciseMachine> | null = null;

  protected dispatchExerciseEvent(event: ExerciseEvent): void {
    const phase = this.exerciseActor?.getSnapshot()?.value;
    console.log(`[EXERCISE-DBG] dispatchExerciseEvent type=${event.type} currentPhase=${phase}`);
    this.exerciseActor?.send(event);
  }

  getExercisePhase(): ExercisePhase | undefined {
    return this.exerciseActor?.getSnapshot()?.value as ExercisePhase | undefined;
  }

  // ---- Helpers ----

  protected getValidObjectiveIndex(): number {
    if (!this.config?.objectives?.length) return 0;
    if (this.currentObjective === undefined || this.currentObjective < 0) {
      return 0;
    }
    return Math.min(this.currentObjective, this.config.objectives.length - 1);
  }

  protected getCurrentObjective(): Objective | undefined {
    if (!this.config?.objectives?.length) return undefined;
    return this.config.objectives[this.getValidObjectiveIndex()];
  }

  protected isLastObjective(): boolean {
    if (!this.config?.objectives?.length) return true;
    return this.getValidObjectiveIndex() === this.config.objectives.length - 1;
  }

  private timestampToSeconds(stamp: Timestamp): number {
    const [min, sec] = stamp.split(':').map(parseFloat);
    return min * 60 + sec;
  }

  showInstruction(instruction: string, position: InstructionPosition = 'Top'): void {
    this.instruction = new Instruction(this, instruction, position);
  }

  debugLog(text: string): void {
    if (process.env.REACT_APP_NODE_ENV === 'prd' || process.env.REACT_APP_NODE_ENV === 'stg')
      return;
    if (!this.debugText)
      this.debugText = this.add.text(200, 100, text, {
        color: '#000',
        fontSize: 22,
      });
    else this.debugText.setText(text);
  }

  setPlaybackRate({ rate: _rate }: { rate: number }) {}

  // ==================================================================
  // Scene lifecycle
  // ==================================================================

  init(config: Config): void {
    this.events.once('shutdown', this.unload, this);

    // Clear stale listeners
    RemoveListener('StartExercise');
    RemoveListener('PauseExercise');
    RemoveListener('MidiOn');
    RemoveListener('MidiOff');
    RemoveListener('SkipExercise');
    RemoveListener('UnloadExercise');
    RemoveListener('ChangePlaybackRate');
    RemoveListener('ToggleExercise');
    RemoveListener('ResumeExercise');
    RemoveListener('UpdateObjective');
    RemoveListener('KeepPracticing');
    this.boundCallbacks = new Map();

    const bind = <T>(name: string, fn: (arg: T) => void): ((arg: T) => void) => {
      this.boundCallbacks.set(name, fn as (...args: never[]) => void);
      return fn;
    };

    // Store config and reset state
    this.config = config;
    this.active = true;
    this.paused = false;
    this.currentObjective = undefined;
    this.numFailedAttempts = 0;
    this.skipped = false;
    this.graded = config.graded !== false;
    this.playAlongPassesThisObjective = 0;
    this.countInStartDelay = undefined;
    this.tapsThisPass = false;
    this.tapsThisObjective = false;
    this.inputBeforeThisObjective = 'none';
    this.passedOnAHeardPass = false;

    // ---- Spawn the state machine ----
    const facade = this.createFacade();
    const dispatchEvent = (event: ExerciseEvent) => this.dispatchExerciseEvent(event);
    const actions = buildSceneActions(facade, dispatchEvent);

    this.exerciseActor?.stop();
    this.exerciseActor = createActor(exerciseMachine.provide({ actions }), {
      input: {
        totalObjectives: config.objectives.length,
        chapterIdx: config.chapterIdx,
        loopsWhilePracticing: this.loopsWhilePracticing(),
      },
    });
    this.exerciseActor.start();

    // ---- Route EventBus events through the machine ----
    ReactToPhaserListeners.StartExercise(
      bind('StartExercise', () => {
        if (isCapacitorNative()) {
          console.log('[EXERCISE-DBG] ExerciseBase StartExercise calling MuseFlowAudio.warmUp()');
          MuseFlowAudio.warmUp().then(() => {
            this.dispatchExerciseEvent({ type: 'START_EXERCISE' });
          });
        } else {
          this.dispatchExerciseEvent({ type: 'START_EXERCISE' });
        }
      }),
      this,
    );
    ReactToPhaserListeners.PauseExercise(
      bind('PauseExercise', () => {
        this.dispatchExerciseEvent({ type: 'PAUSE_EXERCISE' });
      }),
      this,
    );
    ReactToPhaserListeners.MidiOn(
      bind('MidiOn', (data) => this.midiOn(data)),
      this,
    );
    ReactToPhaserListeners.MidiOff(
      bind('MidiOff', (data) => this.midiOff(data)),
      this,
    );
    ReactToPhaserListeners.SkipExercise(
      bind('SkipExercise', () => {
        this.dispatchExerciseEvent({ type: 'SKIP_EXERCISE' });
      }),
      this,
    );
    ReactToPhaserListeners.UnloadExercise(
      bind('UnloadExercise', () => this.stop()),
      this,
    );
    ReactToPhaserListeners.ChangePlaybackRate(
      bind('ChangePlaybackRate', (data) => this.setPlaybackRate(data)),
      this,
    );
    ReactToPhaserListeners.ToggleExercise(
      bind('ToggleExercise', () => this.handleToggle()),
      this,
    );
    ReactToPhaserListeners.ResumeExercise(
      bind('ResumeExercise', () => {
        this.dispatchExerciseEvent({ type: 'RESUME_EXERCISE' });
      }),
      this,
    );
    ReactToPhaserListeners.UpdateObjective(
      bind('UpdateObjective', (data: { chapter: number }) => {
        this.dispatchExerciseEvent({ type: 'UPDATE_OBJECTIVE', chapter: data.chapter });
      }),
      this,
    );
    ReactToPhaserListeners.KeepPracticing(
      bind('KeepPracticing', () => {
        this.dispatchExerciseEvent({ type: 'KEEP_PRACTICING' });
      }),
      this,
    );
  }

  // ==================================================================
  // Ungraded play: the exercise plays itself (see tutorialPlayAlong)
  // ==================================================================

  /** Ungraded: whether Keep practicing runs the objective again and again. */
  protected loopsWhilePracticing(): boolean {
    return false;
  }

  /** Ungraded: how long the Next ring runs once the objective's run is over. */
  protected nextRingMs(): number {
    return NEXT_RING_AFTER_TIMED_RUN_MS;
  }

  /**
   * Whether notes reach the exercise. Ungraded exercises take none: the app
   * cannot hear the player (exercises that take taps hear those: hearsNote).
   */
  protected hearsNotes(): boolean {
    return this.graded;
  }

  /**
   * Whether this exercise takes tap input: a tap on the exercise area, or a
   * computer key, played as a key (see Utils/playAlong/tapInput). Only the
   * exercises played with any key do.
   */
  protected takesTaps(): boolean {
    return false;
  }

  /** Ungraded and an exercise that takes taps: taps are played as keys. */
  protected listensForTaps(): boolean {
    return !this.graded && this.takesTaps();
  }

  /** Whether `note` reaches the exercise: any key when graded, else only a tap it listens for. */
  protected hearsNote(note?: { tap?: boolean }): boolean {
    return this.graded || (this.listensForTaps() && note?.tap === true);
  }

  /** Tap input: the exercise took a tap. The pass running now is graded. */
  protected tapHeard(): void {
    this.tapsThisPass = true;
    this.tapsThisObjective = true;
  }

  /**
   * Whether the pass that just ended is graded: the app heard the player (a
   * MIDI keyboard, or taps in this pass). A graded pass is judged, and passing
   * it shows the pass animation and plays the feedback clip. An ungraded one
   * is not judged and gets neither: the Next ring follows.
   */
  protected passWasHeard(): boolean {
    return this.graded || this.tapsThisPass;
  }

  /** What the player played this objective on, for the chapter-completed event. */
  protected inputUsed(): TutorialInputUsed {
    if (this.graded) return 'midi';
    return this.tapsThisObjective ? 'tap' : 'none';
  }

  /**
   * What the player played the exercise on, over every objective so far:
   * the strongest input heard (MIDI over taps over nothing).
   */
  protected exerciseInputUsed(): TutorialInputUsed {
    return strongerInput(this.inputBeforeThisObjective, this.inputUsed());
  }

  /**
   * Whether moving on from the last objective with Next clears the chapter on
   * a graded pass: ungraded, an earlier objective passed on taps. A skip with
   * a MIDI keyboard is not graded, as it always was.
   */
  protected skipClearsOnAGradedPass(): boolean {
    return !this.graded && this.passedOnAHeardPass;
  }

  /**
   * Tap input: on a touch screen, taps may land a little later than keys (a
   * touch reaches the app later). Widens the late edge of every note's onset.
   */
  protected allowForTouchLatency(cursor: { allowLateOnsetMs: (ms: number) => void }): void {
    if (this.listensForTaps() && isTouchDevice())
      cursor.allowLateOnsetMs(TOUCH_TAP_LATE_ALLOWANCE_MS);
  }

  /** Tap input: the line under the instruction that says how to play. */
  protected showTapHint(): void {
    if (this.listensForTaps()) this.instruction?.showHint(tapInputHint(isTouchDevice()));
  }

  /**
   * Ungraded: start a pass of a timed objective with `startCountIn`. The first
   * pass on an objective waits a moment after its instruction first; a pass
   * after Keep practicing counts straight in (`continuesLoop` true), on the
   * beat the pass before it ended on.
   */
  protected startPlayAlongPass(startCountIn: (continuesLoop: boolean) => void): void {
    const isFirstPass = this.playAlongPassesThisObjective === 0;
    this.playAlongPassesThisObjective += 1;
    this.cancelCountInStartDelay();
    if (!isFirstPass) {
      startCountIn(true);
      return;
    }
    this.countInStartDelay = this.time.delayedCall(TUTORIAL_COUNT_IN_START_DELAY_MS, () => {
      this.countInStartDelay = undefined;
      startCountIn(false);
    });
  }

  private cancelCountInStartDelay(): void {
    this.countInStartDelay?.remove();
    this.countInStartDelay = undefined;
  }

  /**
   * Ungraded: draw the count-in's countdown number and closing ring for the
   * count-in the scene is counting now (null: none, or paused). Graded
   * play draws nothing. Call every frame.
   */
  protected updateCountInCountdown(
    countIn: CountedClicks | null,
    anchor: CountdownAnchor | null,
    entryHead: () => RingTarget | null,
  ): void {
    if (this.graded) {
      this.countInCountdownInstance?.clear();
      return;
    }
    this.countInCountdownInstance ??= new CountInCountdownView(this);
    this.countInCountdownInstance.updateCounted({
      countIn,
      anchor,
      entryHead,
    });
  }

  private destroyCountInCountdown(): void {
    this.countInCountdownInstance?.destroy();
    this.countInCountdownInstance = undefined;
  }

  /** A pause holds the wait before the first count-in, too. */
  private holdCountInStartDelay(held: boolean): void {
    if (this.countInStartDelay) this.countInStartDelay.paused = held;
  }

  showNextRing(): void {
    NotifyReactEvents.NextRingStart({ durationMs: this.nextRingMs() });
  }

  // ==================================================================
  // IExerciseSceneFacade implementation (overridable by subclasses)
  // ==================================================================

  loadObjective(objectiveIndex: number): void {
    this.currentObjective = objectiveIndex;
    const objective = this.config?.objectives[objectiveIndex];
    if (objective) {
      this.showInstruction(objective.text ?? 'Play this line of music.');
    }
    // Subclasses override to build graphics, then call:
    // this.dispatchExerciseEvent({ type: 'OBJECTIVE_LOADED' });
    // Default: immediately loaded (subclass MUST override if async)
    this.dispatchExerciseEvent({ type: 'OBJECTIVE_LOADED' });
  }

  unloadObjective(): void {}

  centreHeight(objectHeight: number): number {
    return this.cameras.main.height / 2 - objectHeight / 2;
  }

  /**
   * Lowest edge of the music this scene is showing, in camera pixels.
   * Undefined for scenes that show no staff.
   */
  protected getNotationBottom(): number | undefined {
    return undefined;
  }

  centreWidth(objectWidth: number): number {
    return this.cameras.main.width / 2 - objectWidth / 2;
  }

  initExercise(_objectiveIndex: number): void {
    // Subclasses override to create cursor, event stream, start metronome
    this.startExercise();
  }

  beginPlayback(_note: number, _velocity: number): void {}

  stopPlayback(): void {}

  evaluatePerformance(): void {
    // Default: pass (subclasses override to check notes)
    this.dispatchExerciseEvent({ type: 'OBJECTIVE_PASSED' });
  }

  notifyShowExercise(): void {
    NotifyReactEvents.ShowExercise(undefined);
  }

  notifyHideExercise(): void {
    console.log('[EXERCISE-DBG] notifyHideExercise');
    NotifyReactEvents.HideExercise();
  }

  notifyExercisePassed(): void {
    console.log(`[EXERCISE-DBG] notifyExercisePassed skipped=${this.skipped}`);
    if (!this.skipped) {
      // A pass clears the chapter: it was heard, so it was graded.
      NotifyReactEvents.ExercisePassed({ inputUsed: this.exerciseInputUsed(), graded: true });
    }
  }

  notifyObjectiveComplete(objectiveIndex: number, isLastObjective: boolean): void {
    console.log(
      `[EXERCISE-DBG] notifyObjectiveComplete idx=${objectiveIndex} isLast=${isLastObjective}`,
    );
    const objective = this.config?.objectives[objectiveIndex];
    if (objective?.videoSkipDestination && isLastObjective) {
      console.log(
        `[EXERCISE-DBG] firing ObjectiveComplete jumpTo=${objective.videoSkipDestination}`,
      );
      NotifyReactEvents.ObjectiveComplete({ jumpTo: objective.videoSkipDestination! });
    }
  }

  notifyObjectiveSkipped(objectiveIndex: number, isLastObjective: boolean): void {
    const objective = this.config?.objectives[objectiveIndex];
    if (objective?.videoSkipDestination) {
      // `isLastObjective` lets React distinguish a within-exercise objective
      // skip (advance the video, the running scene's machine loads the next
      // objective) from a skip that ends the exercise and jumps to the next
      // chapter. The latter must route through the robust chapter-jump path
      // so the next exercise's scene is loaded before playback resumes —
      // otherwise the next chapter's UpdateObjective fires before its scene
      // exists and the exercise never loads.
      NotifyReactEvents.ObjectiveSkipped({
        jumpTo: objective.videoSkipDestination,
        isLastObjective,
        // The last objective ends the exercise: the event describes all of it.
        inputUsed: isLastObjective ? this.exerciseInputUsed() : this.inputUsed(),
        ...(isLastObjective ? { graded: this.skipClearsOnAGradedPass() } : {}),
      });
    }
  }

  requestSkipButton(): void {
    NotifyReactEvents.SkipButtonRequest(undefined);
  }

  showPassAnimation(onComplete: () => void): void {
    // Nothing was heard, so there is nothing to celebrate: no animation, no clip.
    if (!this.passWasHeard()) {
      onComplete();
      return;
    }
    if (!isCapacitorNative()) {
      if (this.sound.locked) {
        this.sound.unlock();
      }
      Tone.start();
    }

    const clipIdx = this.registry.get('clipIdx') as number;
    this.registry.set(
      'clipIdx',
      clipIdx + 1 >= this.registry.get('feedbackClipCount') ? 0 : clipIdx + 1,
    );

    this.instruction?.showResult(true, () => {
      const clipKey = this.registry.get('feedbackClipRandomDistribution')[clipIdx];
      if (isCapacitorNative()) {
        const clipName = feedbackKeyToFilename(clipKey);
        MuseFlowAudio.playSoundEffect({ name: clipName }).then(({ duration }) => {
          this.time.delayedCall(feedbackAdvanceDelayMs(duration), () => onComplete());
        });
      } else {
        // MUS-113: wait the clip's real duration (floored) before advancing,
        // mirroring the native branch, so a clip longer than the old hardcoded
        // 1000ms is no longer cut off when the scene tears down. Use add()
        // rather than play(key) to read the decoded duration, and destroy the
        // instance on completion since add() does not auto-remove it.
        const clip = this.sound.add(clipKey);
        clip.once(Phaser.Sound.Events.COMPLETE, () => clip.destroy());
        clip.play(soundEffectConfig());
        const durationSecs = clip.duration > 0 ? clip.duration : FEEDBACK_DURATION_FALLBACK_SECS;
        this.time.delayedCall(feedbackAdvanceDelayMs(durationSecs), () => onComplete());
      }
    });
  }

  showFailAnimation(_failedAttempts: number): void {
    this.failExercise();
  }

  pauseScene(): void {
    this.paused = true;
    this.pauseExercise();
  }

  resumeScene(): void {
    this.paused = false;
    this.resume();
  }

  playNarration(objectiveIndex: number): void {
    if (!this.config) return;
    const objective = this.config.objectives[objectiveIndex];
    if (!objective?.narrationStartTimestamp || !objective?.narrationEndTimestamp) {
      // No narration timestamps — advance immediately
      this.dispatchExerciseEvent({ type: 'NARRATION_COMPLETE' });
      return;
    }

    const startSecs = this.timestampToSeconds(objective.narrationStartTimestamp);
    const endSecs = this.timestampToSeconds(objective.narrationEndTimestamp);
    const durationMs = (endSecs - startSecs) * 1000;

    if (!isCapacitorNative()) {
      this.playNarrationFromVideo(
        objectiveIndex,
        objective.narrationStartTimestamp,
        objective.narrationEndTimestamp,
        durationMs,
      );
      return;
    }

    if (isCapacitorNative() && this.config.levelNumber !== undefined) {
      const rate = this.config.playbackSpeed ?? 1;
      console.log(
        `[EXERCISE-DBG] playNarration file=level-${this.config.levelNumber}-audio start=${startSecs} end=${endSecs} duration=${durationMs}ms rate=${rate}`,
      );
      MuseFlowAudio.playNarration({
        file: `level-${this.config.levelNumber}-audio`,
        startTime: startSecs,
        endTime: endSecs,
        rate,
      });
    }

    // Schedule NARRATION_COMPLETE after the narration duration (adjusted
    // for playback speed).
    const rate = this.config.playbackSpeed ?? 1;
    const adjustedDuration = durationMs / rate;
    this.time.delayedCall(Math.max(adjustedDuration, 500), () => {
      this.dispatchExerciseEvent({ type: 'NARRATION_COMPLETE' });
    });
  }

  /**
   * Web: the narration lives in the tutorial video, which is paused at the
   * previous objective's StartExercise cue. Ask React to play the segment for
   * this objective; the video's own StartExercise cue at its end pauses the
   * video and starts the exercise, exactly as for the first objective.
   */
  private playNarrationFromVideo(
    objectiveIndex: number,
    from: Timestamp,
    to: Timestamp,
    durationMs: number,
  ): void {
    NotifyReactEvents.PlayNarration({ from, to });
    const longestNarrationMs = durationMs / SLOWEST_VIDEO_PLAYBACK_RATE;
    this.time.delayedCall(longestNarrationMs + WEB_NARRATION_START_FALLBACK_MS, () => {
      if (this.currentObjective !== objectiveIndex) return;
      this.dispatchExerciseEvent({ type: 'NARRATION_COMPLETE' });
    });
  }

  cleanup(): void {
    this.unloadGraphics();
    this.clearEvents();
  }

  unloadGraphics(): void {
    // Subclasses override to destroy scene-specific graphics
  }

  scheduleStop(delay: number = 500): void {
    console.log(`[EXERCISE-DBG] scheduleStop delay=${delay}`);
    this.time.delayedCall(delay, this.stop, [], this);
  }

  // ==================================================================
  // Facade factory — creates the facade bound to this scene
  // ==================================================================

  private createFacade(): IExerciseSceneFacade {
    return {
      loadObjective: (idx) => {
        this.playAlongPassesThisObjective = 0;
        // Fold the objective that ends into the exercise's input first.
        this.inputBeforeThisObjective = this.exerciseInputUsed();
        this.tapsThisObjective = false;
        this.loadObjective(idx);
      },
      unloadObjective: () => this.unloadObjective(),
      initExercise: (idx) => {
        // A new pass: no tap heard in it yet.
        this.tapsThisPass = false;
        // Ungraded: Next (the Skip button) shows from the start.
        if (!this.graded) this.requestSkipButton();
        this.initExercise(idx);
      },
      beginPlayback: (note, velocity) => this.beginPlayback(note, velocity),
      stopPlayback: () => this.stopPlayback(),
      evaluatePerformance: () => this.evaluatePerformance(),
      notifyShowExercise: () => this.notifyShowExercise(),
      notifyHideExercise: () => this.notifyHideExercise(),
      notifyExercisePassed: () => this.notifyExercisePassed(),
      notifyObjectiveComplete: (idx, isLast) => this.notifyObjectiveComplete(idx, isLast),
      notifyObjectiveSkipped: (idx, isLast) => this.notifyObjectiveSkipped(idx, isLast),
      requestSkipButton: () => this.requestSkipButton(),
      showPassAnimation: (cb) => {
        // The objective passed; on taps that is a graded pass of the chapter.
        if (this.passWasHeard()) this.passedOnAHeardPass = true;
        this.showPassAnimation(cb);
      },
      showFailAnimation: (attempts) => this.showFailAnimation(attempts),
      pauseScene: () => {
        this.holdCountInStartDelay(true);
        this.pauseScene();
      },
      resumeScene: () => {
        this.holdCountInStartDelay(false);
        this.resumeScene();
      },
      playNarration: (idx) => this.playNarration(idx),
      cleanup: () => {
        this.cancelCountInStartDelay();
        this.destroyCountInCountdown();
        this.cleanup();
      },
      unloadGraphics: () => {
        this.cancelCountInStartDelay();
        this.destroyCountInCountdown();
        this.unloadGraphics();
      },
      scheduleStop: (delay) => this.scheduleStop(delay),
      showNextRing: () => this.showNextRing(),
    };
  }

  // ==================================================================
  // Legacy methods (kept for subclass compatibility during migration)
  // ==================================================================

  onExerciseShow() {}

  /** @deprecated Use machine transitions instead */
  transition(_skip?: boolean, _last?: boolean): void {}

  midiOn(_data?: { note: number; velocity?: number }): void {}
  midiOff(_data?: { note: number }): void {}

  clearEvents(): void {
    this.removeOwnListener('MidiOn');
    this.removeOwnListener('MidiOff');
    this.removeOwnListener('PauseExercise');
    this.removeOwnListener('UpdateObjective');
    this.removeOwnListener('SkipExercise');
    this.removeOwnListener('UnloadExercise');
    this.removeOwnListener('ToggleExercise');
    this.removeOwnListener('ResumeExercise');
    this.removeOwnListener('KeepPracticing');
  }

  protected removeOwnListener(eventName: string): void {
    const fn = this.boundCallbacks.get(eventName);
    if (fn) {
      EventBus.off(eventName, fn, this);
      this.boundCallbacks.delete(eventName);
    }
  }

  unload(): void {
    console.log('[EXERCISE-DBG] ExerciseBase.unload — stopping actor');
    const wasActive = this.active;
    this.exerciseActor?.stop();
    this.exerciseActor = null;
    this.destroyCountInCountdown();
    this.currentObjective = undefined;
    this.active = false;
    this.removeOwnListener('StartExercise');
    this.removeOwnListener('UpdateObjective');
    this.debugText?.destroy();
    this.debugText = null;
    this.active = false;
    this.failMessage?.destroy();
    this.failMessage = null;
    this.toastMessage?.destroy();
    this.toastMessage = null;
    this.instruction?.destroy();
    this.instruction = null;
    this.registeredKeys.clear();
    this.numFailedAttempts = 0;
    if (this.videoResumeTimeout) {
      this.videoResumeTimeout.destroy();
      this.videoResumeTimeout = undefined;
    }
    this.clearEvents();
    this.time.removeAllEvents();
    this.tweens.killAll();
    if (this.paused) this.scene.stop('PauseMenu');
  }

  // ---- Methods subclasses still use ----

  startExercise(): void {}

  pauseExercise(): void {
    this.paused = true;
    this.scene.launch('PauseMenu');
    NotifyReactEvents.ExercisePaused({});
  }

  resume(): void {
    this.paused = false;
    NotifyReactEvents.ExerciseResumed();
  }

  handleToggle(): void {
    if (this.paused) {
      this.dispatchExerciseEvent({ type: 'RESUME_EXERCISE' });
    } else {
      this.dispatchExerciseEvent({ type: 'PAUSE_EXERCISE' });
    }
  }

  onObjectivePassed(): void {}

  onObjectiveSkipped(): void {}

  onExerciseFail(): void {
    this.restart();
  }

  onExercisePass(): void {}

  restart(): void {}

  onExerciseOver(delay: number = 500): void {
    this.time.delayedCall(delay, this.stop, [], this);
  }

  abstract stop(): void;
  abstract create(): void;

  showToast(text: string): void {
    if (this.waitToast) return;
    this.waitToast = true;
    const dpr = 1;
    this.toastMessage?.destroy();
    this.toastMessage = this.add.text(100, 100, text, {
      fontFamily: 'Lato',
      fontSize: `${32 * dpr}px`,
      color: '#000000',
      align: 'center',
    });
    this.toastMessage.setAlpha(0);
    this.toastMessage.setPosition(
      this.cameras.main.width / 4 - this.toastMessage.width / 2,
      this.cameras.main.height / 4 - this.toastMessage.height / 2,
    );
    this.tweens.add({
      targets: this.toastMessage,
      alpha: 1,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        this.tweens.add({
          targets: this.toastMessage,
          alpha: 0,
          duration: 300,
          delay: 750,
          ease: 'Power2',
          onComplete: () => {
            this.waitToast = false;
          },
          callbackScope: this,
        });
      },
      callbackScope: this,
    });
  }

  failExercise(message?: string): void {
    this.failMessage?.destroy();
    const dpr = 1;
    const camW = this.cameras.main.width;
    const camH = this.cameras.main.height;
    this.failMessage = this.add.text(0, 0, message ?? 'Try again!', {
      fontFamily: 'Lato',
      fontSize: `${Math.min(48, camH * 0.1) * dpr}px`,
      color: '#000',
      fontStyle: 'bold',
    });
    const notationBottom = this.getNotationBottom();
    this.failMessage.setPosition(
      camW / 2 - this.failMessage.width / 2,
      notationBottom === undefined
        ? camH * 0.35 - this.failMessage.height / 2
        : messageTopBelowNotation(notationBottom, this.failMessage.height, camH),
    );
    this.failMessage.setAlpha(0);
    this.tweens.add({
      targets: this.failMessage,
      alpha: 1,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        this.tweens.add({
          targets: this.failMessage,
          alpha: 0,
          duration: 500,
          delay: 1000,
          ease: 'Power2',
          onComplete: () => {
            this.dispatchExerciseEvent({ type: 'RETRY' });
          },
          callbackScope: this,
        });
      },
      callbackScope: this,
    });
  }

  /** @deprecated — subclasses should override passObjective behavior via facade */
  passObjective(_last?: boolean): void {
    this.dispatchExerciseEvent({ type: 'OBJECTIVE_PASSED' });
  }

  skipExercise(): void {
    this.dispatchExerciseEvent({ type: 'SKIP_EXERCISE' });
  }
}
